import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Budget, BudgetDocumentMode } from "./useBudgets";
import type { DocumentField, DocumentFieldInput } from "@/components/admin/documents/types";

// The proposal's "document" layer (richtext / uploaded PDF / fields) — the
// budget-side twin of the contract hooks in useContracts.ts.

export function useUpdateBudgetDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<
        Pick<
          Budget,
          "document_mode" | "richtext_content" | "source_pdf_url" | "pending_client_signers"
        >
      >;
    }) => {
      const { error } = await supabase
        .from("budgets" as never)
        .update(data as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["budgets"] }),
    onError: (e: Error) => toast.error("Erro: " + e.message),
  });
}

// Switching mode drops the other mode's content, placed fields and any
// frozen base PDF so a stale document can never be sent by accident.
export function useSwitchBudgetDocumentMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, mode }: { id: string; mode: BudgetDocumentMode }) => {
      const { error: fieldsErr } = await supabase
        .from("budget_fields" as never)
        .delete()
        .eq("budget_id", id);
      if (fieldsErr) throw fieldsErr;
      const { error } = await supabase
        .from("budgets" as never)
        .update({
          document_mode: mode,
          richtext_content: null,
          source_pdf_url: null,
          base_pdf_url: null,
          base_document_hash: null,
        } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["budgets"] });
      qc.invalidateQueries({ queryKey: ["budget-fields", vars.id] });
      toast.success("Tipo de documento alterado!");
    },
    onError: (e: Error) => toast.error("Erro: " + e.message),
  });
}

export function useUploadBudgetSourcePdf() {
  return useMutation({
    mutationFn: async ({ budgetId, file }: { budgetId: string; file: File }) => {
      const path = `budgets/${budgetId}/source.pdf`;
      const { error: uploadError } = await supabase.storage
        .from("signed-documents")
        .upload(path, file, { contentType: "application/pdf", upsert: true });
      if (uploadError) throw uploadError;
      const { data, error: urlError } = await supabase.storage
        .from("signed-documents")
        .createSignedUrl(path, 60 * 60 * 24 * 365);
      if (urlError) throw urlError;
      return data.signedUrl;
    },
    onError: (e: Error) => toast.error("Erro ao enviar PDF: " + e.message),
  });
}

export function useBudgetFields(budgetId: string | undefined) {
  return useQuery({
    queryKey: ["budget-fields", budgetId],
    queryFn: async () => {
      if (!budgetId) return [];
      const { data, error } = await supabase
        .from("budget_fields" as never)
        .select("*")
        .eq("budget_id", budgetId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as unknown as DocumentField[];
    },
    enabled: !!budgetId,
  });
}

export function useSaveBudgetFields(budgetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: DocumentFieldInput[]) => {
      const { error: delErr } = await supabase
        .from("budget_fields" as never)
        .delete()
        .eq("budget_id", budgetId);
      if (delErr) throw delErr;
      if (fields.length === 0) return;
      const { error: insErr } = await supabase
        .from("budget_fields" as never)
        .insert(fields.map((f) => ({ ...f, budget_id: budgetId })) as never);
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["budget-fields", budgetId] });
      toast.success("Campos salvos!");
    },
    onError: (e: Error) => toast.error("Erro: " + e.message),
  });
}
